class Metrics {
  private metricsGateway = `https://m-w.dev.mobileposse.com`
  // private metricsGateway = `http://localhost:8080`;
  private commonLabelAndValues: Record<string, string | undefined>

  // Batching configuration
  private batchSizeThreshold = 10
  private batchTimeoutDuration = 5000

  // Storage for batched metrics
  private batchedCounters: { name: string; labels: Record<string, string> }[] = []
  private batchedHistograms: { name: string; value: number; labels: Record<string, string> }[] = []
  private batchTimeoutId: number | null = null

  constructor() {
    this.commonLabelAndValues = {
      env: window?.mp_globals?.env?.environment,
      platform: window?.mp_globals?.env?.platform,
      page: window.location.pathname,
      content_domain: window.location.host
    }
  }

  private getFullLabels(additionalLabels: Partial<Record<string, string>>): Record<string, string> {
    const fullLabels: Record<string, string> = {}
    for (const key in this.commonLabelAndValues) {
      const value = this.commonLabelAndValues[key]
      if (value !== undefined) {
        fullLabels[key] = value
      }
    }
    for (const key in additionalLabels) {
      const value = additionalLabels[key]
      if (value !== undefined) {
        fullLabels[key] = value
      }
    }
    return fullLabels
  }

  public histogram(metricName: string, value: number, labelsAndValues: Partial<Record<string, string>>) {
    try {
      const histogramPayload = {
        name: `neptune_content_${metricName}`,
        value,
        labels: this.getFullLabels(labelsAndValues)
      }
      this.batchedHistograms.push(histogramPayload)
      this.scheduleBatchSend()
    } catch (error) {
      console.error('Error preparing histogram metric:', error)
    }
  }

  public counter(metricName: string, labelsAndValues: Partial<Record<string, string>>) {
    try {
      const counterPayload = {
        name: `neptune_content_${metricName}`,
        labels: this.getFullLabels(labelsAndValues)
      }
      this.batchedCounters.push(counterPayload)
      this.scheduleBatchSend()
    } catch (error) {
      console.error('Error preparing counter metric:', error)
    }
  }

  private scheduleBatchSend() {
    if (this.batchedCounters.length + this.batchedHistograms.length >= this.batchSizeThreshold) {
      this.sendBatch()
    } else {
      if ((this.batchedCounters.length > 0 || this.batchedHistograms.length > 0) && this.batchTimeoutId === null) {
        this.batchTimeoutId = window.setTimeout(() => {
          this.sendBatch()
        }, this.batchTimeoutDuration)
      }
    }
  }

  private sendBatch() {
    if (this.batchTimeoutId !== null) {
      window.clearTimeout(this.batchTimeoutId)
      this.batchTimeoutId = null
    }

    if (this.batchedCounters.length === 0 && this.batchedHistograms.length === 0) {
      return // Nothing to send
    }

    const payload = {
      counters: [...this.batchedCounters],
      histograms: [...this.batchedHistograms]
    }

    // Clear local batches immediately after copying them for the payload
    this.batchedCounters = []
    this.batchedHistograms = []

    try {
      fetch(`${this.metricsGateway}/m/b`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      })
        .then(response => {
          if (!response.ok) {
            console.error(`Metrics batch submission failed with status: ${response.status}`)
          }
        })
        .catch(error => {
          console.error('Error sending metrics batch:', error)
        })
    } catch (error) {
      console.error('Error during fetch setup for metrics batch:', error)
    }
  }

  public flush() {
    if (this.batchedCounters.length > 0 || this.batchedHistograms.length > 0) {
      this.sendBatch()
    }
  }
}

// Create a singleton instance of the Metrics class
const metricsInstance = new Metrics()

// Export the instance
export default metricsInstance
